import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { styled } from 'styletron-react';
import { Search, ChevronRight } from 'lucide-react';
import { getPublicEvents, type PublicEvent } from '../../api/publicApi';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useS3Image } from '../../hooks/useS3Image';
import { useLang, pickLocalized } from '../../i18n/publicEvents';
import { LanguageToggle } from '../../components/LanguageToggle';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import { PrivateLockIcon } from '../../components/PrivateLockIcon';
import { isPrivateEvent } from '../../utils/privateEvent';

// --- Styled (matches Flutter explore_screen.dart) ---

const Page = styled('div', {
  minHeight: '100vh',
  backgroundColor: '#fff',
  fontFamily: "'Red Hat Text', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
});

const Content = styled('div', {
  maxWidth: '600px',
  margin: '0 auto',
  padding: '0 8px 60px',
  width: '100%',
  boxSizing: 'border-box',
});

const LogoContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  padding: '10px 0 24px',
});

// Search bar — matches Flutter SearchWidget: pill shape, grey bg, 45px
const SearchBarContainer = styled('div', {
  padding: '0 8px',
  marginBottom: '12px',
});

const SearchBarInner = styled('div', {
  display: 'flex',
  alignItems: 'center',
  height: '45px',
  backgroundColor: '#F3F3F3',
  borderRadius: '22px',
  padding: '0 15px',
  gap: '10px',
});

const SearchInput = styled('input', {
  border: 'none',
  outline: 'none',
  flex: 1,
  fontSize: '15px',
  color: '#000',
  backgroundColor: 'transparent',
  '::placeholder': { color: '#9CA3AF' },
});

// Category pills — matches Flutter ExploreDirectorySlivers
const CategoriesRow = styled('div', {
  display: 'flex',
  overflowX: 'auto',
  paddingTop: '12px',
  paddingBottom: '4px',
  scrollbarWidth: 'none',
  '::-webkit-scrollbar': { display: 'none' },
});

const CategoryPill = styled('button', ({ $selected, $color }: { $selected: boolean; $color: string }) => ({
  flexShrink: 0,
  padding: '0 22px',
  height: '34px',
  borderRadius: '22px',
  border: 'none',
  backgroundColor: $selected ? $color : '#F3F3F3',
  color: $selected ? '#fff' : '#000',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  margin: '8px',
  whiteSpace: 'nowrap' as const,
}));

// List tile — matches Flutter ListTile with leading image, title, subtitle
const ListTile = styled('div', {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  cursor: 'pointer',
  ':hover': { backgroundColor: '#FAFAFA' },
});

const ListTileImage = styled('div', {
  width: '65px',
  height: '65px',
  borderRadius: '8px',
  backgroundColor: '#F3F3F3',
  flexShrink: 0,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ListTileContent = styled('div', {
  flex: 1,
  marginLeft: '16px',
  minWidth: 0,
});

const ListTileTitle = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  color: '#000',
  marginBottom: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
});

const ListTileSubtitle = styled('div', {
  fontSize: '14px',
  color: '#6B7280',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
});

const Divider = styled('div', {
  height: '1px',
  backgroundColor: '#E8E8E8',
  margin: '0 16px',
});

const EmptyState = styled('div', {
  textAlign: 'center',
  padding: '60px 20px',
  color: '#9CA3AF',
});

const Loader = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  padding: '80px 0',
});

// --- Helper ---

const EventThumbnail: React.FC<{ imageKey: string }> = ({ imageKey }) => {
  const { imageUrl } = useS3Image(imageKey);
  if (!imageUrl) return null;
  return <img src={imageUrl} alt="" style={{ width: '65px', height: '65px', objectFit: 'cover', borderRadius: '8px' }} />;
};

// --- Component ---

export const PublicEvents: React.FC = () => {
  usePublicPageScroll();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const { tenant, tenantLogo, tenantName, tenantPrimaryColor, tenantApiKey } = useTenantBranding();
  const { imageUrl: tenantLogoUrl } = useS3Image(tenantLogo);

  const tenantId = tenant?.id || '';

  const { data: events, isLoading } = useQuery({
    queryKey: ['public-events', tenantId, tenantApiKey],
    queryFn: () => getPublicEvents(tenantId, tenantApiKey || undefined),
    enabled: !!tenantId,
  });

  const categories = useMemo(() => {
    if (!events) return [];
    const map = new Map<number, string>();
    events.forEach(e => {
      if (e.event_type?.id && e.event_type?.name) {
        map.set(e.event_type.id, e.event_type.name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [events]);

  const filtered = useMemo(() => {
    if (!events) return [];
    // Only show active events
    let list = events.filter(e => {
      if (e.end_date) return new Date(e.end_date) > new Date();
      return e.active !== false;
    });
    if (selectedCategory !== null) {
      list = list.filter(e => e.event_type?.id === selectedCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.name || e.name_es || '').toLowerCase().includes(q) ||
        (e.name_en || '').toLowerCase().includes(q) ||
        (e.address || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [events, search, selectedCategory]);

  const formatSubtitle = (event: PublicEvent) => {
    if (event.address) return event.address;
    if (event.start_date) {
      const d = new Date(event.start_date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return '';
  };

  return (
    <Page>
      {/* Language toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', maxWidth: '600px', margin: '0 auto', padding: '12px 16px 0', boxSizing: 'border-box' }}>
        <LanguageToggle color={tenantPrimaryColor} />
      </div>

      {/* Logo */}
      <LogoContainer>
        {tenantLogoUrl && (
          <img src={tenantLogoUrl} alt={tenantName} style={{ maxHeight: '100px', maxWidth: '200px', objectFit: 'contain' }} />
        )}
      </LogoContainer>

      <Content>
        {/* Search bar */}
        <SearchBarContainer>
          <SearchBarInner>
            <Search size={20} color="#000" />
            <SearchInput
              placeholder={t('searchByName')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchBarInner>
        </SearchBarContainer>

        {/* Category pills */}
        {categories.length > 0 && (
          <CategoriesRow>
            <CategoryPill
              $selected={selectedCategory === null}
              $color={tenantPrimaryColor}
              onClick={() => setSelectedCategory(null)}
            >
              {t('all')}
            </CategoryPill>
            {categories.map(cat => (
              <CategoryPill
                key={cat.id}
                $selected={selectedCategory === cat.id}
                $color={tenantPrimaryColor}
                onClick={() => setSelectedCategory(prev => prev === cat.id ? null : cat.id)}
              >
                {cat.name}
              </CategoryPill>
            ))}
          </CategoriesRow>
        )}

        {/* Events list */}
        {isLoading ? (
          <Loader>
            <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: tenantPrimaryColor, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </Loader>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: 4 }}>{t('noEventsTitle')}</div>
            <div>{t('noEventsBody')}</div>
          </EmptyState>
        ) : (
          filtered.map((event, idx) => (
            <React.Fragment key={event.id}>
              <ListTile onClick={() => navigate(`/events/${event.id}`)}>
                <ListTileImage>
                  {(event.logo_url || event.background_img_url) && (
                    <EventThumbnail imageKey={event.logo_url || event.background_img_url || ''} />
                  )}
                </ListTileImage>
                <ListTileContent>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <ListTileTitle>{pickLocalized(event, 'name', lang) || event.name}</ListTileTitle>
                    {isPrivateEvent(event) && (
                      <PrivateLockIcon label={t('privateEventLock')} size={15} style={{ color: '#6B7280', marginBottom: '4px' }} />
                    )}
                  </div>
                  <ListTileSubtitle>{formatSubtitle(event)}</ListTileSubtitle>
                </ListTileContent>
                <ChevronRight size={20} color="#D1D5DB" />
              </ListTile>
              {idx < filtered.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
      </Content>
    </Page>
  );
};
